import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/Card";
import { LoadingSkeleton } from "@/components/loading-skeleton";
import { formatPublicCategoryLabel } from "@/features/public-site/public-site.utils";

type CategoriesSectionProps = {
  categoryCounts: Array<{
    category: string;
    count: number;
  }>;
  isLoading?: boolean;
};

export function CategoriesSection({
  categoryCounts,
  isLoading = false,
}: CategoriesSectionProps) {
  return (
    <section id="categories" className="mt-12 rounded-sm border border-primary/20 bg-card px-6 py-8 shadow-sm md:px-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">
            Popular Categories
          </p>
          <h2 className="text-3xl font-black tracking-tight text-foreground">
            Explore what the market is stocking right now.
          </h2>
        </div>
        <Button asChild variant="ghost" className="hidden rounded-sm text-primary md:inline-flex">
          <Link href="/shop">See all</Link>
        </Button>
      </div>

      {isLoading ? (
        <LoadingSkeleton
          variant="cards"
          count={4}
          gridClassName="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4"
          showMedia={false}
        />
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {categoryCounts.length > 0 ? (
            categoryCounts.map((item) => (
              <Card key={item.category} className="border-border bg-muted shadow-sm">
                <CardContent className="space-y-3 p-5">
                  <div className="text-lg font-semibold text-foreground">
                    {formatPublicCategoryLabel(item.category)}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.count} products available</p>
                  <Button asChild variant="outline" className="rounded-sm border-primary/30 text-primary">
                    <Link href={`/shop?category=${item.category}`}>Explore Now</Link>
                  </Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="rounded-sm border border-dashed border-border p-8 text-sm text-muted-foreground">
              No categories available yet.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
